/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { fontFace } from "@fluentui/merge-styles";
import {
  makeStyles,
  shorthands,
  Text,
  tokens,
} from "@fluentui/react-components";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import * as React from "react";
import type { Tip } from "./Tips.web.data";

// This line is needed to register a new font-face in addition to default Segoe UI fonts
// See https://github.com/microsoft/fluentui/blob/b03a47260f977d472f34e3402dc89d3e3d56f760/packages/merge-styles/README.md#registering-fonts
fontFace({
  fontFamily: "Aptos Serif",
  src: 'url("https://fs.microsoft.com/fs/4.35/rawguids/28013589931")',
  fontStyle: "normal",
  fontDisplay: "swap",
});

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minHeight: "17.5rem",
    height: "100%",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.flex(1, 0, "0px"),
    ...shorthands.borderRadius("16px"),
    ...shorthands.gap("1.75rem"),
    ...shorthands.padding("20px", "0px", "0px", "0px"),
    "@media (max-width: 1424px)": {
      ...shorthands.gap("16px"),
      width: "100%",
      height: "auto",
      minHeight: "auto",
    },
  },

  headerRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.gap("8px"),
    ...shorthands.padding("0px", "24px", "0px", "24px"),
  },

  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "137.5%",
    ...shorthands.margin("0px"),
  },

  subTitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: ".875rem",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "142.857%",

    "@media (min-width: 860px)": {
      minHeight: "2.5rem",
    },
  },

  bodyRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    ...shorthands.padding("0px", "1.5rem", "0rem", "1.5rem"),

    "@media (max-width: 1424px)": {
      ...shorthands.padding("0px", "24px", "24px", "24px"),
    },
  },

  normal: {
    color: tokens.colorBrandForegroundOnLight,
    fontSize: "2rem",
    fontStyle: "normal",
    fontFamily: "Aptos Serif",
    fontWeight: 400,
    lineHeight: "120%",

    "@media (max-width: 540px)": {
      fontSize: "1.75rem",
    },
  },

  highlight: {
    color: tokens.colorPalettePurpleForeground2,
    fontFamily: "Aptos Serif",
    fontSize: "2rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "120%",

    "@media (max-width: 540px)": {
      fontSize: "1.75rem",
    },
  },

  strikeThrough: {
    ...shorthands.textDecoration("line-through"),
    color: tokens.colorPalettePurpleForeground2,
  },
});

const allowedHtmlTags = ["b", "s"];

export const TipCard: React.FC<Tip> = ({ title, subtitle, content }) => {
  const styles = useStyles();
  const sanitizedContent = DOMPurify.sanitize(content, {
    IN_PLACE: true,
    ALLOWED_TAGS: allowedHtmlTags,
  });
  const sanitizedContentElement = parse(sanitizedContent, {
    replace: (domNode: any) => {
      if (domNode.tagName === "s") {
        return (
          <span className={styles.strikeThrough}>
            <span className={styles.highlight}>{domNode.children[0].data}</span>
          </span>
        );
      }

      if (domNode.tagName === "b") {
        return (
          <span className={styles.highlight}>{domNode.children[0].data}</span>
        );
      }
    },
  });

  return (
    <div className={styles.root}>
      <div className={styles.headerRoot}>
        <Text as="h3" className={styles.title}>
          {title}
        </Text>
        <Text className={styles.subTitle}>{subtitle}</Text>
      </div>
      <div className={styles.bodyRoot}>
        <Text className={styles.normal}>{sanitizedContentElement}</Text>
      </div>
    </div>
  );
};
